import detectIt from 'detect-it';
import Cookies from 'js-cookie';
import EzUser from 'init/init-user';

declare global {
  interface Window {
    Cypress:any
    EzPrefetchLinksCache:string[]
  }
}

const environments = {
  production: ['production'],
  dev: ['development', 'test'],
  staging: ['staging'],
  test: ['test'],
};

export function isEnvironment(env: ('production' | 'dev' | 'staging' | 'test')) {
  return environments[env].includes(document.body.dataset.env);
}

export function ezDebounce(fn, time) {
  let timeout;

  return (...args) => {
    const functionCall = () => fn.apply(this, args);

    clearTimeout(timeout);
    timeout = setTimeout(functionCall, time);
  };
}

export function getContentType() {
  return Cookies.get('content_type') || 'vector';
}

export function getKeyByValue(object, value) {
  return Object.keys(object).find((key) => object[key] === value);
}

export function getWindowWidth() {
  return window.innerWidth
  || document.documentElement.clientWidth
  || document.body.clientWidth;
}

export function getWindowHeight() {
  return window.innerHeight
  || document.documentElement.clientHeight
  || document.body.clientHeight;
}

export function createElementFromHTML(htmlString) {
  const div = document.createElement('div');
  div.innerHTML = htmlString.trim();

  return div.firstChild;
}

export function addListenerMulti(el, s, fn) {
  const passive = detectIt.passiveEvents ? { passive: true } : false;

  s.split(' ').forEach((e) => { el.addEventListener(e, fn, passive); });
}

export function removeListenerMulti(el, s, fn) {
  s.split(' ').forEach((e) => { el.removeEventListener(e, fn, false); });
}

export function isSafari() {
  return navigator.vendor && navigator.vendor.indexOf('Apple') > -1
         && navigator.userAgent
         && navigator.userAgent.indexOf('CriOS') === -1
         && navigator.userAgent.indexOf('FxiOS') === -1;
}

export function detectEdge() {
  const ua = window.navigator.userAgent;

  // Edge (IE 12+) => return version number
  const edge = ua.indexOf('Edge/');
  if (edge > 0) {
    return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
  }

  return false;
}

export function detectCypress() {
  if (window.Cypress) return true;
  return false;
}

export function shouldBypassCaptcha() {
  if (detectCypress()) return true;

  return false;
}

export function getChromeVersion() {
  const raw = navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
  return raw ? parseInt(raw[2], 10) : false;
}

export function isScrolledIntoView(elem, partial = false) {
  const rect = elem.getBoundingClientRect();
  const elemTop = rect.top;
  const elemBottom = rect.bottom;

  // Only completely visible elements return true:
  let isVisible = (elemTop >= 0) && (elemBottom <= window.innerHeight);
  // Partially visible elements return true:
  if (partial) {
    isVisible = elemTop < window.innerHeight && elemBottom >= 0;
  }
  return isVisible;
}

export function scrollIntoViewWithOffset({
  selector,
  offset = 0,
  behavior = 'auto',
}:{ selector:string, offset?:number, behavior?:ScrollBehavior }) {
  const element = document.querySelector(selector) as HTMLElement;
  const elementOffset = element.offsetTop;
  let targetPosition = elementOffset - offset;

  return new Promise<void>((resolve, reject) => {
    let scrollListener;

    const failed = setTimeout(() => {
      window.removeEventListener('scroll', scrollListener);

      reject(new Error('Never reached scroll position!'));
    }, 2000);

    scrollListener = (evt) => {
      if (typeof evt === 'undefined') {
        return;
      }

      const doc = document.documentElement;
      const scrollPos = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);

      targetPosition = elementOffset - offset;

      if (scrollPos >= targetPosition) {
        window.removeEventListener('scroll', scrollListener);
        clearTimeout(failed);

        resolve();
      }
    };

    window.addEventListener('scroll', scrollListener);

    window.scrollTo({ top: targetPosition, behavior });
  });
}

export function scrolledIntoViewWithOffset(selector, offset = 0) {
  const element = document.querySelector(selector);
  const docViewTop = window.pageYOffset || document.documentElement.scrollTop;
  const docViewBottom = docViewTop + window.innerHeight;
  const elementTop = element.offsetTop;
  const elementBottom = elementTop + (element.offsetHeight - offset);
  return elementBottom <= docViewBottom;
}

export function isHomepage() {
  const bodyClass = document.body.classList;
  return bodyClass.contains('photos-home_photos') || bodyClass.contains('vectors-home_vectors') || bodyClass.contains('home-home_vectors') || bodyClass.contains('home-home_videos') || bodyClass.contains('videos-home_videos');
}

export function isSearchPage() {
  const bodyClass = document.body.classList;
  return bodyClass.contains('searches-show');
}

export function isSearchByImagePage() {
  const bodyClass = document.body.classList;
  return bodyClass.contains('searches-show_by_image');
}

export function ezFadeOut(element, speed, callback = () => { /* do nothing */ }) {
  const s = element.style;

  s.opacity = 1;

  /* eslint-disable func-names */
  (function fade() {
    s.opacity -= 0.1;

    if (s.opacity < 0) {
      s.display = 'none';

      callback();
    } else {
      setTimeout(fade, speed);
    }
  }());
  /* eslint-enable func-names */
}

export function getFormattedPageValue(currentPageElem) {
  let value = currentPageElem.innerHTML; // grab the contents
  value = value.replace(/\D/g, ''); // remove all non digit characters
  const intValue = parseInt(value, 10);
  return intValue || 1; // default to 1 if value is 0
}

export function postAjax(eventName, dataHash, addPushState) {
  const postAjaxEvent = new CustomEvent(eventName, {
    detail: { dataHash, addPushState },
  });
  window.dispatchEvent(postAjaxEvent);
}

export function triggerCustomEvent(eventName, detailsHash = {}, elem:(HTMLElement | Window) = window) {
  const event = new CustomEvent(eventName, {
    detail: detailsHash,
  });

  elem.dispatchEvent(event);
}

export function makeQueryParams(dataHash, exclude = []) {
  let params = Object.keys(dataHash)
    .filter((key) => {
      if (exclude.includes(encodeURIComponent(key))) {
        return false;
      }

      return true;
    })
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(dataHash[key])}`,
    )
    .join('&');

  if (params.length) {
    params = `?${params}`;
  }

  return params;
}

export function resetUrlWithParam(param, value) {
  const url = window.location.href.split('?')[0];
  window.location = `${url}?${param}=${value}` as (string | Location) & Location;
}

export function printElem(elem, title, printStylesheet) {
  const windowLocation = {
    left: (window.screen.availWidth / 2) - 50,
    top: (window.screen.availHeight / 2) - 300,
  };

  const mywindow = window.open('', 'PRINT', `height=700, width=1000, left=${windowLocation.left}, top=${windowLocation.top}`);

  mywindow.document.write(`<html><head><title>${title}</title>`);
  mywindow.document.write('<style media="print">body { display: block!important; }</style>');

  if (printStylesheet) {
    mywindow.document.write(printStylesheet);
  }

  mywindow.document.write('</head><body style="display: none;">');
  mywindow.document.write(`<h1>${title}</h1>`);

  mywindow.document.write(elem.innerHTML);

  mywindow.document.write('</body></html>');

  mywindow.document.close(); // necessary for IE >= 10
  mywindow.focus(); // necessary for IE >= 10*/

  setTimeout(() => { // Set a timeout to allow print stylesheet to load up
    mywindow.print();
    mywindow.close();
  }, 400); // Increasing this from 300 seems to help print style loading issue

  return true;
}

export function smoothScroll(ypos:number, element:Window | HTMLElement = window) {
  setTimeout(() => {
    element.scrollTo({ top: ypos, behavior: 'smooth' });
  }, 1);
}

type TemplateItem = {
  [key: string]: any
}

export function parseTemplate(string, item:TemplateItem) {
  let newString = string;

  // Exception to replace occurences of data-href="*" with href="*"
  // This prevents bots from crawling hrefs found in templates
  if (item.url) newString = string.replace(/data-href="[^"]*"/g, `href="${item.url}"`);
  if (item.src) newString = string.replace(/data-src="[^"]*"/g, `src="${item.src}"`);
  if (item.url && item.src) newString = string.replace(/data-href="[^"]*"/g, `href="${item.url}"`).replace(/data-src="[^"]*"/g, `src="${item.src}"`);

  return newString.replace(/\{\{(\w+)\}\}/g, (a, b) => item[b]);
}

export function trim(s:string):string {
  return (s || '').replace(/^\s+|\s+$/g, '');
}

export function showHideItems(items:HTMLElement[], showHide:string, displayShow = 'block') {
  const displayValue = showHide === 'show' ? displayShow : 'none';

  items.forEach((item:HTMLElement) => {
    item.style.display = displayValue;
  });
}

export function featureEnabled(feature:string) {
  const normalizedFlag = feature.replace(/-/g, '_');

  return EzUser.enabledFlags.includes(normalizedFlag);
}

export function splitTestEnabled(splitTest:string) {
  const normalizedSplitTest = splitTest.replace(/-/g, '_');

  return EzUser.splitTestName === normalizedSplitTest && EzUser.splitTestVariant === 'test';
}

export function isUserLoggedIn() {
  return document.body.classList.contains('is-logged-in');
}

export function resolvePath(path, obj, separator = '.') {
  const properties = Array.isArray(path) ? path : path.split(separator);
  return properties.reduce((prev, curr) => prev && prev[curr], obj);
}

export function addLoadingOverlay(resultsContainer, fadeOut) {
  if (!resultsContainer) return;

  const overlayHtml = '<div class="results-loading-overlay" data-results-loading="true"><div class="ez-spinner"></div></div>';

  resultsContainer.insertAdjacentHTML(
    'afterBegin',
    overlayHtml,
  );

  if (fadeOut) {
    const filterLoadingOverlay = document.querySelector('[data-results-loading=true]');

    if (filterLoadingOverlay) {
      ezFadeOut(filterLoadingOverlay, 30, () => {
        filterLoadingOverlay.parentNode.removeChild(filterLoadingOverlay);
      });
    }
  }
}

export function getCurrentPageParam(url:string = null) {
  let params;

  if (url) {
    const baseUrl = window.location.href;
    params = new URL(url, baseUrl).searchParams;
  } else {
    const queryString = window.location.search.slice(1);
    params = new URLSearchParams(queryString);
  }

  if (params.has('page')) {
    return params.get('page');
  }

  return null;
}

export function primeForPostLogin(postLoginCookieName, postLoginCookieValue) {
  const sessionData = {
    post_login_cookie_name: postLoginCookieName,
    post_login_cookie_value: postLoginCookieValue,
  };

  Cookies.set('primer_for_post_login_cookie', JSON.stringify(sessionData));
}

// Add leading zero to a number
export function padZero(num) {
  return (`000000000${num}`).substr(-2);
}

export function persistInputFocus(input, callback) {
  const keepFocus = input === document.activeElement;
  callback();
  if (keepFocus) input.focus();
}

export function showLoaderAnimation(targetElement) {
  targetElement.classList.add('is-loading');
}

export function hideLoaderAnimation(targetElement) {
  targetElement.classList.remove('is-loading');
}

export function gaClientId() {
  const value = Cookies.get('_ga');
  if (!value) return undefined;

  const pattern = 'GA1.1.';
  return value.substr(pattern.length);
}

export const capitalize = (string) => string && string[0].toUpperCase() + string.slice(1);

export const parseBoolean = (value) => {
  if (!value) return false;

  const boolValue = value.toString().toLowerCase();

  return !(boolValue === '0' || boolValue === 'false');
};

export const truncateZerosOrNah = (number:number):string => {
  if (Math.round(number) !== number) return number.toFixed(2);

  return number.toString();
};

export const roundToDecimals = (num: number, decimalPlaces = 2):number => {
  const placesCalc = 10 ** decimalPlaces;
  return Math.round((num + Number.EPSILON) * placesCalc) / placesCalc;
};

export const getPercentage = (a:number, b:number, decimalPlaces = 2):number => {
  if (b === 0) return 0;
  // a is what percent of b
  const result = (a / b) * 100;

  return roundToDecimals(result, decimalPlaces);
};

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const objectAttrsToSnakeCase = (obj: Record<string, any>) => {
  const snakeCaseObj = {};

  Object.keys(obj).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const snakeCaseKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      snakeCaseObj[snakeCaseKey] = obj[key];
    }
  });

  return snakeCaseObj;
};

export const textareaAdjust = ({ initialHeight, input }:{ initialHeight:number, input:HTMLInputElement }) => {
  if (input.scrollHeight <= initialHeight) return;

  if (input.value) {
    input.style.height = '1px';
    input.style.height = `${input.scrollHeight}px`;
  } else {
    input.style.height = `${initialHeight}px`;
  }
};

window.EzPrefetchLinksCache = [];

export const injectPrefetchLink = ({
  src,
  asAttr = null,
  linkClass = null,
}:{
  src:string
  asAttr:('document' | 'image' | 'video' | 'script' | 'style' | 'font' | null)
  linkClass:(null | string)
}) => {
  if (!window.EzPrefetchLinksCache.includes(src)) {
    const link = document.createElement('link');
    link.setAttribute('rel', 'prefetch');
    link.setAttribute('href', src);

    if (asAttr) link.setAttribute('as', asAttr);

    if (linkClass) link.setAttribute('class', linkClass);

    document.getElementsByTagName('head')[0].appendChild(link);

    window.EzPrefetchLinksCache.push(src);
  }
};

export const isVideoUrl = (url) => (
  url.split('.').pop() === 'mp4' || url.split('.').pop() === 'webm');
